<script setup>
/**
 * THE WORDS ON THE CARD — every question of one task, in one language, on one screen.
 * THE DESIGN, §4 ⑪ and §6.3.
 *
 * ── WHAT IT IS FOR ────────────────────────────────────────────────────────────────────
 * Writing one missing Arabic question at a time, from inside seven different step
 * screens, is seven navigations to fix one defect. This is the sweep: the whole task's
 * card words side by side, the source language on one side, the language being written
 * on the other, and one action that proposes the missing ones in a batch.
 *
 * It is a COMPONENT WITH PROPS, not a page. The composer mounts it as a step of its rail
 * and owns the draft; this file owns no task state at all and writes nothing — it emits
 * one row's new words and the parent decides where they go (`withCardWords` in
 * `helpers/task-arabic.js` is the writer, so the one place that knows which wire field a
 * row means is the one place that decided the row existed).
 *
 * ── THE CARVE-OUT, VISIBLE ────────────────────────────────────────────────────────────
 * `schema.ts:84-96` puts `questionEn`/`questionAr` on `ask` and `choose` and DELIBERATELY
 * not on `choose_live`. A live list's rows are fetched while the conversation is running,
 * so it can never be a static field, and `flow.ts` refuses it with `runtime_rows` whatever
 * anybody wrote. An `ask` for a time of day is refused the same way, before the words are
 * even looked at ("a form has no time picker"). So neither is listed here, and neither is
 * ever counted as missing — a count that cannot be driven to zero is a permanent warning,
 * not a piece of work. The gap it leaves in the step numbers is answered by ONE line at
 * the foot of the screen, because an unexplained gap is its own small mystery.
 *
 * ── THE MODEL NEVER EDITS IN PLACE ────────────────────────────────────────────────────
 * "Translate the N missing" PROPOSES. Each suggestion arrives in its own panel beside the
 * box it is for, and the box is byte-for-byte what the owner typed until she presses the
 * button — the discipline `OptimizeField.vue` established for the wording assistant, and
 * it matters more here: these words are not a prompt for a model to interpret, they are
 * sent to a customer exactly as they stand.
 *
 * **The button does not exist unless somebody can answer it.** `translator` is a function
 * the parent supplies; with none, there is no button. A control that cannot work must not
 * be on screen. The composer supplies one since P-7.1: the batch form of the sidecar's
 * translate route, so a sweep is ONE call and ONE charge, at most 20 rows (the batch
 * bound; press again for the rest). The price is on the button and confirmed in place
 * before anything runs (P-5.12); a row with no words in the other language to translate
 * from is not sent.
 *
 * ── NO OVERLAY ────────────────────────────────────────────────────────────────────────
 * Every panel opens in place. The teleported overlays reach the Vuex store during render
 * and throw after a unit test has already resolved, which can only fail an exit code.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import AiConfirmInline from '../AiConfirmInline.vue';
import WasloInput from '../WasloInput.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { useAiConfirm } from '../../composables/useAiConfirm.js';
import {
  cardWords,
  missingRowsOf,
  otherLang,
} from '../../helpers/task-arabic.js';
import { TRANSLATE_BATCH_MAX } from '../../helpers/missing-language.js';

const props = defineProps({
  /** A spine (`{rows}`), a definition (`{steps}`) or a bare array of steps. */
  source: { type: [Object, Array], default: () => ({ rows: [] }) },
  /** The language being written here. The other one is the source column. */
  lang: { type: String, default: 'ar' },
  /**
   * `(rows, credits) => Promise<Array<{stepId, optionId?, kind?, text}> |
   * { proposals: Array<…>, charged: number|null }>`, supplied by a parent that has something
   * to ask. `credits` is the price the owner confirmed. Absent, the default, and the batch
   * action does not render.
   */
  translator: { type: Function, default: null },
  /** Where the missing-language warning is (the Check screen), or '' for no link. */
  warningLink: { type: [String, Object], default: '' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);

const { t } = useWasloI18n();

const sweep = computed(() => cardWords(props.source, props.lang));
const missingRows = computed(() => missingRowsOf(sweep.value));
const other = computed(() => otherLang(props.lang));

// P-7.1 with P-5.12: one batch, one price, confirmed in place.
const accountId = useWasloAccountId();
const charge = useAiConfirm({ accountId, op: 'suggest_translations' });
const { receiptText } = charge;

/** What one press sends: rows with words to translate FROM, at most one batch. */
const batch = computed(() =>
  missingRows.value
    .filter(row => row.other !== '')
    .slice(0, TRANSLATE_BATCH_MAX)
);

const translateLabel = computed(() =>
  charge.label(
    t('WASLO.TASKS.ARABIC.TRANSLATE', { count: batch.value.length }),
    batch.value.length
  )
);

const tagOf = lang => t(`WASLO.COMMON.LANG_TAG.${lang.toUpperCase()}`);
const nameOf = lang => t(`WASLO.COMMON.LANG_NAME.${lang.toUpperCase()}`);

const written = computed(() => sweep.value.total - sweep.value.missing);

const rowKey = row =>
  `${row.kind}:${row.stepId}:${row.optionId === null ? '' : row.optionId}`;

// ── what a row has to say about itself ──────────────────────────────────────────────
// Three states, three different costs to a customer, three different sentences. Reading
// them off one `state` rather than off "is the box empty" is what keeps a step whose own
// NAME is already in this language out of the warning column: `flow.ts` builds its field
// from that name, so calling it missing would be a warning nobody can ever clear.
const noteFor = row => {
  if (row.state === 'from_name') {
    return {
      tone: 'ok',
      text: t('WASLO.TASKS.ARABIC.STATE.FROM_NAME', { name: row.name }),
    };
  }
  if (row.state === 'falls_back') {
    return {
      tone: 'warn',
      text: t('WASLO.TASKS.ARABIC.STATE.FALLS_BACK', {
        language: nameOf(props.lang),
      }),
    };
  }
  if (row.state === 'missing') {
    return {
      tone: 'warn',
      text:
        row.kind === 'option'
          ? t('WASLO.TASKS.ARABIC.STATE.NO_LABEL')
          : t('WASLO.TASKS.ARABIC.STATE.MISSING', {
              language: nameOf(props.lang),
            }),
    };
  }
  return null;
};

const onInput = (row, text) => {
  emit('update', {
    stepId: row.stepId,
    optionId: row.optionId,
    kind: row.kind,
    lang: row.lang,
    text,
  });
};

// ── proposed, never applied ─────────────────────────────────────────────────────────
const proposals = ref({});
const isWorking = ref(false);
const failure = ref('');

const hasProposals = computed(() => Object.keys(proposals.value).length > 0);

const proposalFor = row => proposals.value[rowKey(row)] ?? '';

const sameRow = (row, proposed) =>
  row.stepId === proposed?.stepId &&
  (row.optionId === null ? '' : row.optionId) ===
    (proposed?.optionId === null || proposed?.optionId === undefined
      ? ''
      : proposed.optionId) &&
  (proposed?.kind === undefined || proposed.kind === row.kind);

const translate = async () => {
  if (!props.translator || isWorking.value || batch.value.length === 0) return;
  const rows = [...batch.value];
  isWorking.value = true;
  failure.value = '';
  try {
    const outcome = await charge.confirmAndRun({
      count: rows.length,
      run: credits => props.translator(rows, credits),
    });
    // Cancel costs nothing and changes nothing.
    if (outcome.cancelled) return;
    const answered = outcome.result;
    const list = Array.isArray(answered) ? answered : answered?.proposals;
    const next = {};
    (Array.isArray(list) ? list : []).forEach(proposed => {
      const text =
        typeof proposed?.text === 'string' ? proposed.text.trim() : '';
      if (text === '') return;
      const match = rows.find(row => sameRow(row, proposed));
      if (match) next[rowKey(match)] = text;
    });
    proposals.value = next;
    if (Object.keys(next).length === 0) {
      failure.value = t('WASLO.TASKS.ARABIC.TRANSLATE_NONE');
    }
  } catch {
    // One sentence for every failure mode: her next move is the same whichever it was,
    // and her words are untouched in all of them.
    proposals.value = {};
    failure.value = t('WASLO.TASKS.ARABIC.TRANSLATE_FAILED');
  } finally {
    isWorking.value = false;
  }
};

const forget = row => {
  const key = rowKey(row);
  proposals.value = Object.fromEntries(
    Object.entries(proposals.value).filter(([at]) => at !== key)
  );
};

const accept = row => {
  const text = proposalFor(row);
  forget(row);
  if (text !== '') onInput(row, text);
};

const discardAll = () => {
  proposals.value = {};
  failure.value = '';
};
</script>

<template>
  <section class="flex flex-col gap-6" data-waslo-arabic-screen>
    <header class="flex flex-col gap-2">
      <h2 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('WASLO.TASKS.ARABIC.TITLE') }}
      </h2>
      <p dir="auto" class="mb-0 text-sm leading-6 text-n-slate-11">
        {{ t('WASLO.TASKS.ARABIC.DESCRIPTION') }}
      </p>
    </header>

    <div
      v-if="sweep.total > 0"
      class="flex flex-wrap items-center justify-between gap-3 p-3 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
    >
      <!--
        `dir="auto"` on every sentence that mixes a number with words: a paragraph of
        English copy inside an Arabic page (and the reverse) is reordered by the bidi
        algorithm, and the visible symptom is a number that jumps to the far end of its
        own sentence. One attribute per sentence, resolved from its own first strong
        character, is the only thing that fixes it for both accounts at once.
      -->
      <p
        dir="auto"
        class="mb-0 text-sm text-n-slate-12"
        data-waslo-arabic-progress
      >
        <span>
          {{
            t('WASLO.TASKS.ARABIC.PROGRESS', {
              written,
              total: sweep.total,
            })
          }}
        </span>
        <span v-if="sweep.missing > 0" class="text-n-slate-11">
          ·
          {{ t('WASLO.TASKS.ARABIC.LEFT', { count: sweep.missing }) }}
          <router-link
            v-if="warningLink"
            :to="warningLink"
            class="underline text-n-blue-11"
            data-waslo-arabic-warning-link
          >
            {{ t('WASLO.TASKS.ARABIC.SEE_WARNING') }}
          </router-link>
        </span>
        <span v-else class="text-n-slate-11">
          · {{ t('WASLO.TASKS.ARABIC.ALL_DONE') }}
        </span>
      </p>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="translator && batch.length > 0"
          :label="
            isWorking
              ? t('WASLO.TASKS.ARABIC.TRANSLATE_WORKING')
              : translateLabel
          "
          icon="i-lucide-languages"
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="isWorking"
          :disabled="disabled || isWorking"
          data-waslo-arabic-translate
          @click="translate"
        />
        <Button
          v-if="hasProposals"
          :label="t('WASLO.TASKS.ARABIC.DISCARD_ALL')"
          size="sm"
          variant="link"
          color="slate"
          data-waslo-arabic-discard-all
          @click="discardAll"
        />
      </div>
    </div>

    <AiConfirmInline :controller="charge" />

    <p
      v-if="failure"
      dir="auto"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-arabic-failure
    >
      {{ failure }}
    </p>
    <p
      v-if="receiptText"
      class="mb-0 text-xs text-n-slate-11"
      data-waslo-ai-receipt
    >
      {{ receiptText }}
    </p>

    <!-- Nothing to write: the normal answer for a task made only of actions and messages. -->
    <div
      v-if="sweep.total === 0"
      class="flex flex-col gap-1 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
      data-waslo-arabic-empty
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.TASKS.ARABIC.EMPTY') }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.TASKS.ARABIC.EMPTY_BODY') }}
      </p>
    </div>

    <!--
      THE ONLY TWO-COLUMN SCREEN IN THE PRODUCT. A CSS grid rather than a table, and
      grid columns follow the writing direction on their own, so the source language is
      always on the reading-start side in both directions with no physical utility and no
      `ltr:`/`rtl:` pair to keep in step.
    -->
    <div v-else class="flex flex-col gap-6">
      <div
        v-for="group in sweep.groups"
        :key="group.stepId"
        class="flex flex-col gap-4 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
        :data-waslo-arabic-group="group.stepId"
      >
        <p
          class="mb-0 text-xs font-medium tracking-wide uppercase text-n-slate-10"
        >
          {{ t('WASLO.TASKS.ARABIC.STEP', { position: group.position }) }}
        </p>

        <div
          v-for="row in [group.question, ...group.options]"
          :key="rowKey(row)"
          class="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-x-6"
          :data-waslo-arabic-row="rowKey(row)"
          :data-waslo-arabic-state="row.state"
        >
          <!-- The source side: what the words already say, read-only. -->
          <div class="flex flex-col min-w-0 gap-1">
            <span class="text-xs font-medium text-n-slate-10" dir="auto">
              {{ tagOf(other)
              }}<template v-if="row.kind === 'option'">
                · {{ t('WASLO.TASKS.ARABIC.OPTION_LABEL') }}</template
              >
            </span>
            <p
              dir="auto"
              class="mb-0 text-sm leading-6 break-words text-n-slate-12"
              data-waslo-arabic-source
            >
              {{ row.other }}
            </p>
          </div>

          <!-- The side being written. -->
          <div class="flex flex-col min-w-0 gap-1">
            <WasloInput
              :model-value="row.text"
              :label="tagOf(lang)"
              :disabled="disabled"
              :data-waslo-arabic-input="rowKey(row)"
              @update:model-value="text => onInput(row, text)"
            />

            <p
              v-if="noteFor(row)"
              dir="auto"
              class="mb-0 text-xs"
              :class="
                noteFor(row).tone === 'warn'
                  ? 'text-n-ruby-9'
                  : 'text-n-slate-11'
              "
              data-waslo-arabic-note
            >
              {{ noteFor(row).text }}
            </p>

            <!-- PROPOSED, NOT APPLIED. The box above is untouched until the button. -->
            <div
              v-if="proposalFor(row)"
              class="flex flex-col gap-2 p-3 mt-1 outline outline-1 rounded-lg outline-n-weak bg-n-alpha-black2"
              data-waslo-arabic-proposal
            >
              <p class="mb-0 text-xs font-medium text-n-slate-12">
                {{ t('WASLO.TASKS.ARABIC.PROPOSED') }}
              </p>
              <p
                dir="auto"
                class="mb-0 text-sm leading-6 break-words text-n-slate-12"
              >
                {{ proposalFor(row) }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('WASLO.TASKS.ARABIC.PROPOSED_NOTE') }}
              </p>
              <div class="flex flex-wrap gap-2">
                <Button
                  :label="t('WASLO.TASKS.ARABIC.ACCEPT')"
                  size="sm"
                  data-waslo-arabic-accept
                  @click="accept(row)"
                />
                <Button
                  :label="t('WASLO.TASKS.ARABIC.DISCARD')"
                  size="sm"
                  variant="faded"
                  color="slate"
                  data-waslo-arabic-discard
                  @click="forget(row)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--
      Why the step numbers have gaps in them. Not a warning, and never counted: none of
      these steps could be a field on a card however carefully anybody wrote it.
    -->
    <p
      v-if="sweep.offScreen > 0"
      dir="auto"
      class="mb-0 text-xs leading-5 text-n-slate-10"
      data-waslo-arabic-offscreen
    >
      {{ t('WASLO.TASKS.ARABIC.OFF_SCREEN', { count: sweep.offScreen }) }}
    </p>
  </section>
</template>
